import ForgotPasswordPage from '@app/pages/ForgotPasswordPage';
import LoginPage from '@app/pages/LoginPage';
import NewPasswordPage from '@app/pages/NewPasswordPage';
import SecurityCodePage from '@app/pages/SecurityCodePage';
import React from 'react';
import { BrowserRouter, useRoutes } from 'react-router-dom';

// no lazy loading for auth pages to avoid flickering
const AuthLayout = React.lazy(() => import('@app/components/layouts/AuthLayout/AuthLayout'));

import ChatInboxLayout from '@app/components/chat-inbox/ChatInboxLayout';
import WorkerLayout from '@app/components/worker/WorkerLayout';
import ClientLayout from '@app/components/client/ClientLayout';
import DealRoomLayout from '@app/components/deal-room/DealRoomLayout';
import MainLayout from '@app/components/layouts/main/MainLayout/MainLayout';
import ProfileLayout from '@app/components/profile/ProfileLayout';
import RequireAuth from '@app/components/router/RequireAuth';
import { ADMIN_BASE_BATH } from '@app/constants/variables';
import { withLoading } from '@app/hocs/withLoading.hoc';
import DealRoomLandingPage from '@app/pages/deal-room/DealRoomLandingPage';
import ZoneLandingPage from '@app/pages/ZoneLandingPage';
// import ChatBotPage from '@app/pages/ChatBotPage';
import RolesPage from '@app/pages/RolesPage';
import TranslationPage from '@app/pages/TranslationPage';

const ServerErrorPage = React.lazy(() => import('@app/pages/ServerErrorPage'));
const DashboardPage = React.lazy(() => import('@app/pages/DashboardPage'));
const Error404Page = React.lazy(() => import('@app/pages/Error404Page'));
const WorkerPage = React.lazy(() => import('@app/pages/worker/WorkerPage'));
const AccessDeniedPage = React.lazy(() => import('@app/pages/AccessDeniedPage'));
const Logout = React.lazy(() => import('./Logout'));
const PersonalInfoPage = React.lazy(() => import('@app/pages/PersonalInfoPage'));
const WorkerInfoPage = React.lazy(() => import('@app/pages/worker/WorkerInfoPage'));
const NoteInfoPage = React.lazy(() => import('@app/pages/worker/NoteInfoPage'));
const FilePage = React.lazy(() => import('@app/pages/file/FilePage'));
const DealRoomPage = React.lazy(() => import('@app/pages/deal-room/DealRoomPage'));
const DealRoomInfoPage = React.lazy(() => import('@app/pages/deal-room/DealRoomInfoPage'));
const DealRoomAttributePage = React.lazy(() => import('@app/pages/deal-room/DealRoomAttributePage'));
const DealRoomInsightPage = React.lazy(() => import('@app/pages/deal-room/DealRoomInsightPage'));
const DealRoomExternalFilePage = React.lazy(() => import('@app/pages/deal-room/DealRoomExternalFilePage'));
const DealRoomChatPage = React.lazy(() => import('@app/pages/deal-room/DealRoomChatPage'));
const MouseflowEngagementPage = React.lazy(() => import('@app/pages/deal-room/MouseflowEngagementPage'));
const ChatInboxPage = React.lazy(() => import('@app/pages/chat-inbox/ChatInboxPage'));
const ClientPage = React.lazy(() => import('@app/pages/client/ClientPage'));
const ClientInfoPage = React.lazy(() => import('@app/pages/client/ClientInfoPage'));

const Dashboard = withLoading(DashboardPage);
const ZoneLanding = withLoading(ZoneLandingPage);
const DealRoomLanding = withLoading(DealRoomLandingPage);
// const ChatBot = withLoading(ChatBotPage);
const Translation = withLoading(TranslationPage);
const Role = withLoading(RolesPage);
const Worker = withLoading(WorkerPage);
const Client = withLoading(ClientPage);
const DealRoom = withLoading(DealRoomPage);
const ServerError = withLoading(ServerErrorPage);
const Error404 = withLoading(Error404Page);
const AccessDenied = withLoading(AccessDeniedPage);
const AuthLayoutFallback = withLoading(AuthLayout);
const LogoutFallback = withLoading(Logout);

// Profile
const PersonalInfo = withLoading(PersonalInfoPage);

// worker
const WorkerInfo = withLoading(WorkerInfoPage);
const NoteInfo = withLoading(NoteInfoPage);

// client
const ClientInfo = withLoading(ClientInfoPage);

// file
const File = withLoading(FilePage);

// deal room
const DealRoomInfo = withLoading(DealRoomInfoPage);
const DealRoomAttribute = withLoading(DealRoomAttributePage);
const DealRoomInsight = withLoading(DealRoomInsightPage);
const DealRoomExternalFile = withLoading(DealRoomExternalFilePage);
const DealRoomChat = withLoading(DealRoomChatPage);
const MouseflowEngagement = withLoading(MouseflowEngagementPage);

// chat inbox
const ChatInbox = withLoading(ChatInboxPage);

const Router = () => {
  const routes = createRoutes();

  const element = useRoutes(routes);

  return element;
};

const createUserRoutesWithRole = (role: string) => {
  return {
    path: role,
    children: [
      {
        path: '',
        element: <Worker />,
      },
      {
        path: '',
        element: <WorkerLayout />,
        children: [
          {
            path: 'personal-info',
            element: <WorkerInfo />,
          },
          {
            path: 'personal-info/:id',
            element: <WorkerInfo />,
          },
          {
            path: 'note-info/:id',
            element: <NoteInfo />,
          },
        ],
      },
    ],
  };
};

const createClientRoutesWithRole = () => {
  return {
    path: 'clients',
    children: [
      {
        path: '',
        element: <Client />,
      },
      {
        path: '',
        element: <ClientLayout />,
        children: [
          {
            path: 'client-info',
            element: <ClientInfo />,
          },
          {
            path: 'client-info/:id',
            element: <ClientInfo />,
          },
        ],
      },
    ],
  };
};

const createAuthRoutes = () => [
  {
    path: 'login',
    element: <LoginPage />,
  },
  {
    path: 'forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: 'security-code',
    element: <SecurityCodePage />,
  },
  {
    path: 'new-password',
    element: <NewPasswordPage />,
  },
];

const createDealRoom = () => {
  return {
    path: 'deal-rooms',
    children: [
      {
        path: '',
        element: <DealRoom />,
      },
      {
        path: 'insight/:id',
        element: <DealRoomInsight />,
      },
      {
        path: 'engagement',
        element: <MouseflowEngagement />,
      },
      {
        path: '',
        element: <DealRoomLayout />,
        children: [
          {
            path: 'deal-room-info/:id',
            element: <DealRoomInfo />,
          },
          {
            path: 'deal-room-attributes/:id',
            element: <DealRoomAttribute />,
          },
          {
            path: 'deal-room-external-files/:id',
            element: <DealRoomExternalFile />,
          },
          {
            path: 'deal-room-chat/:id',
            element: <DealRoomChat />,
          },
        ],
      },
    ],
  };
};

const createChatInbox = () => {
  return {
    path: 'chats',
    element: <ChatInboxLayout />,
    children: [
      {
        path: '',
        element: <></>,
      },
      {
        path: ':dealRoomId',
        element: <ChatInbox />,
      },
    ],
  };
};

const createRoutes = () => {
  const admins = createUserRoutesWithRole('admins');
  admins.children?.[1]?.children?.splice(2, 4);
  return [
    {
      path: '/',
      element: <ZoneLanding />,
    },
    {
      path: '/:id/page/:key',
      element: <DealRoomLanding />,
    },
    // {
    //   path: '/chat-bot',
    //   element: <ChatBot />,
    // },
    {
      path: ADMIN_BASE_BATH,
      element: (
        <RequireAuth>
          <MainLayout />
        </RequireAuth>
      ),
      children: [
        { path: ADMIN_BASE_BATH, element: <Dashboard /> },
        {
          path: 'profile',
          element: <ProfileLayout />,
          children: [
            {
              path: 'personal-info',
              element: <PersonalInfo />,
            },
          ],
        },
        {
          path: 'translations',
          element: <Translation />,
        },
        {
          path: 'roles',
          element: <Role />,
        },
        {
          path: 'files',
          element: <File />,
        },
        createUserRoutesWithRole('workers'),
        createClientRoutesWithRole(),
        admins,
        createDealRoom(),
        createChatInbox(),
        {
          path: 'server-error',
          element: <ServerError />,
        },
      ],
    },
    {
      path: '/auth',
      element: <AuthLayoutFallback />,
      children: createAuthRoutes(),
    },
    {
      path: '/logout',
      element: <LogoutFallback />,
    },
    {
      path: '/accessDenied',
      element: <AccessDenied />,
    },
    {
      path: '*',
      element: <Error404 />,
    },
  ];
};

export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <Router />
    </BrowserRouter>
  );
};
